import { Component } from '@theme/component';

/**
 * @typedef {Object} AxCommunitySessionsRefs
 * @property {HTMLElement} [carousel]
 */

/**
 * NM Community sessions carousel.
 * Swiper Element initialised programmatically from an SSR fallback (no FOUC,
 * works without JS). Mirrors the ax-sliders-products pattern.
 *
 * @extends {Component<AxCommunitySessionsRefs>}
 */
class AxCommunitySessions extends Component {
  /** @type {any} */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroySwiper();
  }

  async #initSwiper() {
    await customElements.whenDefined('swiper-container');

    const { carousel } = this.refs;
    if (!carousel || this.#swiper) return;

    const swiperEl = /** @type {HTMLElement & { swiper: any; initialize: () => void }} */ (
      carousel.querySelector('swiper-container.ax-community-sessions__swiper')
    );
    const fallback = carousel.querySelector('[data-ax-fallback]');
    if (!swiperEl || !fallback) return;

    const slideElements = [...fallback.children];
    if (!slideElements.length) return;

    // Reserve height so the layout doesn't collapse during the swap (CLS).
    const reservedHeight = carousel.getBoundingClientRect().height;
    if (reservedHeight > 0) {
      carousel.style.minHeight = `${reservedHeight}px`;
    }

    for (const slideEl of slideElements) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slideEl);
      swiperEl.appendChild(swiperSlide);
    }

    fallback.remove();

    const gapDesktop = parseInt(swiperEl.dataset.gapDesktop ?? '0', 10) || 0;
    const gapMobile = parseInt(swiperEl.dataset.gapMobile ?? '0', 10) || 0;

    Object.assign(swiperEl, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      spaceBetween: gapMobile,
      breakpoints: {
        1000: { spaceBetween: gapDesktop },
      },
      pagination: { clickable: true },
    });

    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    requestAnimationFrame(() => {
      carousel.style.minHeight = '';
    });

    this.#swiper = swiperEl.swiper;
    this.#swiper?.update();
  }

  #destroySwiper() {
    if (!this.#swiper) return;
    try {
      this.#swiper.destroy(true, true);
    } catch (_) {
      // Swiper can throw during teardown if already destroyed.
    }
    this.#swiper = null;
  }
}

if (!customElements.get('ax-community-sessions')) {
  customElements.define('ax-community-sessions', AxCommunitySessions);
}
